/**
 * Dashboard Redux Slice - Developer dashboard state management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardService, PublishedFunction, DeveloperAnalytics, ApiKey, UserAccount } from '../../services/dashboard';

interface DashboardState {
  // Functions
  myFunctions: PublishedFunction[];
  functionsLoading: boolean;
  functionsPage: number;
  functionsHasMore: boolean;

  // Analytics
  analytics: DeveloperAnalytics | null;
  analyticsLoading: boolean;

  // API Keys
  apiKeys: ApiKey[];
  apiKeysLoading: boolean;

  // Profile
  profile: UserAccount | null;
  profileLoading: boolean;
}

const initialState: DashboardState = {
  myFunctions: [],
  functionsLoading: false,
  functionsPage: 1,
  functionsHasMore: true,

  analytics: null,
  analyticsLoading: false,

  apiKeys: [],
  apiKeysLoading: false,

  profile: null,
  profileLoading: false
};

export const loadMyFunctions = createAsyncThunk(
  'dashboard/loadMyFunctions',
  async (params: { page: number; limit: number }, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getMyFunctions(params.page, params.limit);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load functions');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load functions');
    }
  }
);

export const loadAnalytics = createAsyncThunk(
  'dashboard/loadAnalytics',
  async (period: '7d' | '30d' | '90d' | '1y' = '30d', { rejectWithValue }) => {
    try {
      const response = await dashboardService.getDeveloperAnalytics(period);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load analytics');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load analytics');
    }
  }
);

export const loadApiKeys = createAsyncThunk(
  'dashboard/loadApiKeys',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getApiKeys();
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load API keys');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load API keys');
    }
  }
);

export const createApiKey = createAsyncThunk(
  'dashboard/createApiKey',
  async (config: {
    name: string;
    permissions: string[];
    rateLimit?: { requests: number; period: 'minute' | 'hour' | 'day' };
    expiresIn?: number;
  }, { rejectWithValue }) => {
    try {
      const response = await dashboardService.createApiKey(config);
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to create API key');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create API key');
    }
  }
);

export const deleteApiKey = createAsyncThunk(
  'dashboard/deleteApiKey',
  async (keyId: string, { rejectWithValue }) => {
    try {
      const response = await dashboardService.deleteApiKey(keyId);
      if (response.status === 'success') {
        return keyId;
      }
      throw new Error('Failed to delete API key');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete API key');
    }
  }
);

export const loadUserProfile = createAsyncThunk(
  'dashboard/loadUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await dashboardService.getUserProfile();
      if (response.status === 'success' && response.data) {
        return response.data;
      }
      throw new Error('Failed to load profile');
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load profile');
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearFunctions: (state) => {
      state.myFunctions = [];
      state.functionsPage = 1;
      state.functionsHasMore = true;
    }
  },
  extraReducers: (builder) => {
    // My Functions
    builder
      .addCase(loadMyFunctions.pending, (state) => {
        state.functionsLoading = true;
      })
      .addCase(loadMyFunctions.fulfilled, (state, action) => {
        state.functionsLoading = false;

        if (action.payload.page === 1) {
          state.myFunctions = action.payload.functions;
        } else {
          state.myFunctions.push(...action.payload.functions);
        }

        state.functionsPage = action.payload.page;
        state.functionsHasMore = action.payload.hasMore;
      })
      .addCase(loadMyFunctions.rejected, (state) => {
        state.functionsLoading = false;
      });

    // Analytics
    builder
      .addCase(loadAnalytics.pending, (state) => {
        state.analyticsLoading = true;
      })
      .addCase(loadAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
      })
      .addCase(loadAnalytics.rejected, (state) => {
        state.analyticsLoading = false;
      });

    // API Keys
    builder
      .addCase(loadApiKeys.pending, (state) => {
        state.apiKeysLoading = true;
      })
      .addCase(loadApiKeys.fulfilled, (state, action) => {
        state.apiKeysLoading = false;
        state.apiKeys = action.payload;
      })
      .addCase(loadApiKeys.rejected, (state) => {
        state.apiKeysLoading = false;
      });

    builder
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.apiKeys.push(action.payload);
      });

    builder
      .addCase(deleteApiKey.fulfilled, (state, action) => {
        state.apiKeys = state.apiKeys.filter(key => key.id !== action.payload);
      });

    // Profile
    builder
      .addCase(loadUserProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(loadUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(loadUserProfile.rejected, (state) => {
        state.profileLoading = false;
      });
  }
});

export const { clearFunctions } = dashboardSlice.actions;
export default dashboardSlice.reducer;